import { useContentMotion } from "../lib/useContentMotion";
import { useLayoutEffect, useRef, type ReactNode } from "react";
import { hubSettingsHref, type Account } from "./api";
import type { HubAgent } from "../lib/api";
import type { Theme } from "../lib/appearance";
import { OwnerNavigation, type OwnerDestination } from "../shell/OwnerNavigation";
import type { StudioWorkspaceState } from "../lib/studioNavigation";
import { connectionHref } from "../connections/navigation";
import { agentIdFrom } from "../lib/base";

export function HubFrame({
  account,
  active,
  agents = null,
  children,
  workspaceAgent,
  workspaceState,
  workspaces,
  agentDestinations,
  notice,
}: {
  account: Account | null;
  active: OwnerDestination | "create" | "connect" | "workspace";
  agents?: HubAgent[] | null;
  /**
   * What the old top bar held for the Hub's own pages. The page has no bar now: the appearance choice and the
   * account menu belong to the navigation, which reads them from the caller.
   */
  onLogout?: () => void;
  theme?: Theme;
  onTheme?: (theme: Theme) => void;
  children: ReactNode;
  workspaceAgent?: HubAgent;
  workspaceState?: StudioWorkspaceState;
  workspaces?: ReactNode;
  agentDestinations?: Record<string, string>;
  notice?: ReactNode;
}) {
  const content = useRef<HTMLElement>(null);
  useContentMotion(content, active, active !== "workspace");
  const currentRoute = workspaceAgent?.id === agentIdFrom(location.pathname) ? location.hash : undefined;
  const accountId = account?.id, currentAgentId = workspaceAgent?.id;
  const params = new URLSearchParams(currentRoute?.split("?")[1]);
  const sessionId = params.get("session_id") || params.get("session") || workspaceState?.sessionId;
  const lastWorkspace = useRef<{ accountId: string; agentId: string; href: string } | null>(null);
  // Home has no active Agent. Keep its settings entry tied to the last actual
  // Agent address, never to a retained frame's background readings.
  useLayoutEffect(() => {
    if (!accountId || !currentAgentId || !currentRoute) return;
    lastWorkspace.current = { accountId, agentId: currentAgentId,
      href: hubSettingsHref(currentAgentId, connectionHref(undefined, sessionId, currentRoute)) };
  }, [accountId, currentAgentId, currentRoute, sessionId]);
  const recent = lastWorkspace.current;
  const settingsHref = workspaceAgent ? hubSettingsHref(workspaceAgent.id,
    connectionHref(undefined, sessionId, currentRoute ?? "#/today"))
    : active === "settings" && location.hash.startsWith("#/settings") ? `/${location.hash}`
      : recent && recent.accountId === accountId && agents?.some(agent => agent.id === recent.agentId) ? recent.href : undefined;
  return (
    <div className={`app hub-app hub-view-${active}`}>
      <OwnerNavigation agents={agents} active={active === "create" || active === "connect" || active === "workspace" ? "agents" : active}
        discovery guest={!account}
        agentDestinations={agentDestinations}
        settingsHref={settingsHref}
        currentAgent={workspaceAgent?.id} currentPanel={workspaceState?.panelKey}
        currentRoute={currentRoute}
        sessionId={workspaceState?.sessionId} activePanels={workspaceState?.panels} version={workspaceState?.version}
        connected={workspaceAgent?.online}
        accountName={account?.name || "你"} />
      <div className="body">
        {notice ? <div className="hub-global-notice">{notice}</div> : null}
        <main ref={content} className="hub-main" hidden={active === "workspace" && Boolean(workspaceAgent)}>{children}</main>
        {workspaces}
      </div>
    </div>
  );
}
